import { AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Auth, authState, signOut, User } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { Subject, catchError, combineLatest, firstValueFrom, of, switchMap, takeUntil } from 'rxjs';
import { Boat } from '../../models/boat.model';
import { AuthService } from '../../services/auth.service';
import { Collapse } from 'bootstrap';
import { AccountContextService } from '../../services/account-context.service';
import { NotificationsService } from '../../services/notifications.service';
import { AppNotification } from '../../models/notification.model';
import { AccountRole } from '../../models/account-member.model';
import { AccountMembership } from '../../models/account-membership.model';
import { PublicBrandingService } from '../../services/public-branding.service';
import { FirestoreService } from '../../services/firestore.service';
import { ConnectivityService } from '../../services/connectivity.service';

interface AccountSwitcherOption {
  accountId: string;
  accountName: string;
  roleLabel: string;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css']
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() title: string = '';
  user: User | null = null;
  private profilePhotoUrl: string | null = null;
  private companyLogoUrl: string | null = null;
  private companyName: string | null = null;
  readonly isAdmin = toSignal(this.authService.isAdmin$, { initialValue: false });
  canAdminister$ = this.accountContextService.canAdminister$;
  readonly newNotificationsCount = this.notificationsService.newNotificationsCount;
  readonly notifications = this.notificationsService.notifications;
  readonly notificationsPreviewLimit = 5;
  private readonly activeMembershipsSignal = toSignal(this.accountContextService.activeMemberships$, { initialValue: [] });
  private readonly activeMembershipSignal = toSignal(this.accountContextService.activeMembership$, { initialValue: null });
  readonly accountOptions = computed<AccountSwitcherOption[]>(() => this.activeMembershipsSignal().map(membership => ({
    accountId: membership.accountId,
    accountName: membership.accountName?.trim() || 'Untitled account',
    roleLabel: this.getRoleLabel(membership.role)
  })));
  readonly hasMultipleAccounts = computed(() => this.accountOptions().length > 1);
  readonly isOnline = toSignal(this.connectivityService.isOnline$, { initialValue: navigator.onLine });
  private readonly accountBoats = toSignal(
    this.accountContextService.activeAccountId$.pipe(
      switchMap(accountId => accountId
        ? this.firestoreService.getBoats(accountId).pipe(catchError(() => of([] as Boat[])))
        : of([] as Boat[]))
    ),
    { initialValue: [] as Boat[] }
  );
  readonly maintenanceNav = computed(() => {
    const boats = this.accountBoats().filter(boat => !boat.deleted && !!boat.id);
    if (boats.length === 1) {
      return { link: ['/boats', boats[0].id], queryParams: {}, fragment: 'maintenance' };
    }
    return { link: ['/boats'], queryParams: { focus: 'maintenance' }, fragment: undefined };
  });
  readonly activeAccountName = computed(() => {
    const activeMembership = this.activeMembershipSignal();
    if (!this.isActiveMembership(activeMembership)) {
      return 'Personal account';
    }
    return activeMembership.accountName?.trim() || 'Untitled account';
  });
  readonly userRoleLabel = computed(() => {
    const membership = this.activeMembershipSignal();
    if (!this.isActiveMembership(membership)) {
      return 'Free user';
    }
    return this.getRoleLabel(membership.role);
  });
  private destroy$ = new Subject<void>();
  @ViewChild('navbarCollapse') navbarCollapse?: ElementRef<HTMLElement>;
  private collapseInstance?: Collapse;
  isNavbarOpen = false;
  isNotificationsDropdownOpen = false;
  isUserDropdownOpen = false;
  isMobileUserMenuOpen = false;
  isMobileNotificationsMenuOpen = false;
  private readonly collapseShownHandler = () => {
    this.isNavbarOpen = true;
  };
  private readonly collapseHiddenHandler = () => {
    this.isNavbarOpen = false;
    this.isMobileUserMenuOpen = false;
    this.isMobileNotificationsMenuOpen = false;
  };

  constructor(
    private readonly auth: Auth,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly accountContextService: AccountContextService,
    private readonly notificationsService: NotificationsService,
    private readonly publicBrandingService: PublicBrandingService,
    private readonly firestoreService: FirestoreService,
    private readonly connectivityService: ConnectivityService
  ) {
  }

  ngOnInit() {
    combineLatest([authState(this.auth), this.authService.user$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([firebaseUser, profile]) => {
        this.user = firebaseUser;
        const profilePhotoUrl = firebaseUser?.uid === profile?.uid ? profile?.photoUrl?.trim() ?? '' : '';
        this.profilePhotoUrl = profilePhotoUrl.length ? profilePhotoUrl : null;
      });

    combineLatest([
      this.accountContextService.activeSubscriptionProfile$,
      this.publicBrandingService.brandingProfile$
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([activeProfile, publicProfile]) => {
        const activeLogoUrl = activeProfile?.companyInformation?.logoUrl?.trim() ?? '';
        const publicLogoUrl = publicProfile?.companyInformation?.logoUrl?.trim() ?? '';
        const resolvedLogoUrl = activeLogoUrl || publicLogoUrl;
        this.companyLogoUrl = resolvedLogoUrl.length ? resolvedLogoUrl : null;

        const activeCompanyName = activeProfile?.companyInformation?.name?.trim() ?? '';
        const publicCompanyName = publicProfile?.companyInformation?.name?.trim() ?? '';
        const resolvedCompanyName = activeCompanyName || publicCompanyName;
        this.companyName = resolvedCompanyName.length ? resolvedCompanyName : null;
      });

  }

  ngAfterViewInit(): void {
    if (this.navbarCollapse?.nativeElement) {
      this.collapseInstance = Collapse.getOrCreateInstance(this.navbarCollapse.nativeElement, { toggle: false });
      this.navbarCollapse.nativeElement.addEventListener('shown.bs.collapse', this.collapseShownHandler);
      this.navbarCollapse.nativeElement.addEventListener('hidden.bs.collapse', this.collapseHiddenHandler);
    }
  }

  toggleNavbar(): void {
    this.collapseInstance?.toggle();
  }

  closeNavbar(): void {
    this.collapseInstance?.hide();
    this.isNavbarOpen = false;
    this.isMobileUserMenuOpen = false;
    this.isMobileNotificationsMenuOpen = false;
  }

  toggleNotificationsDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isMobileNotificationsMenuOpen = false;
    this.isUserDropdownOpen = false;
    this.isNotificationsDropdownOpen = !this.isNotificationsDropdownOpen;
  }

  closeNotificationsDropdown(): void {
    this.isNotificationsDropdownOpen = false;
  }

  toggleUserDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isNotificationsDropdownOpen = false;
    this.isUserDropdownOpen = !this.isUserDropdownOpen;
  }

  closeUserDropdown(): void {
    this.isUserDropdownOpen = false;
  }

  toggleMobileUserMenu(): void {
    this.isMobileNotificationsMenuOpen = false;
    this.isMobileUserMenuOpen = !this.isMobileUserMenuOpen;
  }

  toggleMobileNotificationsMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isMobileUserMenuOpen = false;
    this.isNotificationsDropdownOpen = false;
    this.isUserDropdownOpen = false;
    this.isMobileNotificationsMenuOpen = !this.isMobileNotificationsMenuOpen;
  }

  handleUserMenuClick(): void {
    this.closeUserDropdown();
    this.closeNavbar();
  }

  switchAccount(accountId: string): void {
    if (!accountId || this.activeMembershipSignal()?.accountId === accountId) {
      return;
    }
    this.accountContextService.setActiveAccount(accountId);
    this.closeUserDropdown();
    this.closeNavbar();
    this.router.navigate(['/dashboard']);
  }

  handleViewAll(): void {
    this.closeNotificationsDropdown();
    this.isMobileNotificationsMenuOpen = false;
    this.closeNavbar();
  }

  get recentNotifications(): AppNotification[] {
    return this.notifications().slice(0, this.notificationsPreviewLimit);
  }

  trackNotification(index: number, notification: AppNotification): string | number | undefined {
    return notification.id ?? notification.title ?? index;
  }

  async logout() {
    const uid = this.auth.currentUser?.uid;
    const email = this.auth.currentUser?.email ?? null;

    this.closeUserDropdown();
    this.closeNavbar();

    if (uid) {
      await firstValueFrom(
        this.firestoreService.logUserActivity(uid, 'logout', { email }).pipe(
          catchError(err => {
            console.error('Failed to log activity:', err);
            return of(null);
          })
        )
      );
    }

    await signOut(this.auth);
    this.router.navigate(['/login']);
  }

  private isActiveMembership(membership: AccountMembership | null): membership is AccountMembership {
    if (!membership) {
      return false;
    }
    return !membership.status || membership.status === 'active';
  }

  isActiveAccount(accountId: string): boolean {
    return this.activeMembershipSignal()?.accountId === accountId;
  }

  trackAccount(index: number, account: AccountSwitcherOption): string {
    return account.accountId || `${account.accountName}-${index}`;
  }

  private getRoleLabel(role: AccountRole): string {
    const roleLabels: Record<AccountRole, string> = {
      owner: 'Owner',
      admin: 'Administrator',
      operator: 'Operator',
      viewer: 'Viewer'
    };
    return roleLabels[role] ?? 'Free user';
  }

  get userDisplayName(): string {
    if (this.user?.displayName) {
      return this.user.displayName;
    }

    if (this.user?.email) {
      return this.user.email.split('@')[0];
    }

    return 'Account';
  }

  get userPhotoUrl(): string {
    return this.profilePhotoUrl || this.user?.photoURL || 'https://i.pravatar.cc/80?img=3';
  }

  get headerLogoUrl(): string {
    return this.companyLogoUrl || 'assets/yachtapp-logo.png';
  }

  get headerLogoAlt(): string {
    if (this.companyName) {
      return `${this.companyName} Logo`;
    }
    return `${this.title} Logo`;
  }

  get blogUrl(): string {
    const hostname = globalThis?.location?.hostname ?? '';
    const isFirebasePreview = hostname.endsWith('.web.app') || hostname.endsWith('.firebaseapp.com');

    if (isFirebasePreview) {
      return 'https://blog.yachtapp.net/';
    }

    return 'https://blog.yachtapp.net/';
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;

    if (this.isNotificationsDropdownOpen && !target?.closest('.notifications')) {
      this.isNotificationsDropdownOpen = false;
    }

    if (this.isUserDropdownOpen && !target?.closest('.userbox')) {
      this.isUserDropdownOpen = false;
    }

    if (this.isMobileNotificationsMenuOpen && !target?.closest('.mobile-quick-actions') && !target?.closest('#mobileNotificationsMenu')) {
      this.isMobileNotificationsMenuOpen = false;
    }

    if (this.isMobileUserMenuOpen && !target?.closest('.mobile-user-summary') && !target?.closest('#mobileUserMenu')) {
      this.isMobileUserMenuOpen = false;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.navbarCollapse?.nativeElement) {
      this.navbarCollapse.nativeElement.removeEventListener('shown.bs.collapse', this.collapseShownHandler);
      this.navbarCollapse.nativeElement.removeEventListener('hidden.bs.collapse', this.collapseHiddenHandler);
    }
    this.collapseInstance?.dispose();
  }
}
